/**
 * Language Context for NL/EN localization
 *
 * - Persists language preference in localStorage
 * - Provides t() translation function
 * - Default language: Dutch (nl)
 */

import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Language, translate } from '../i18n/translations';

const LANGUAGE_KEY = 'user_language';
const DEFAULT_LANGUAGE: Language = 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Load from localStorage on init
    const stored = localStorage.getItem(LANGUAGE_KEY);
    if (stored === 'nl' || stored === 'en') {
      return stored;
    }
    return DEFAULT_LANGUAGE;
  });

  // Persist to localStorage when language changes
  const setLanguage = useCallback((lang: Language) => {
    localStorage.setItem(LANGUAGE_KEY, lang);
    setLanguageState(lang);
  }, []);

  // Translation function
  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      return translate(language, key, params);
    },
    [language]
  );

  // Update html lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Re-export Language type for convenience
export type { Language };
